/**
 * Home / Boat layout customization — localStorage-backed user preferences.
 *
 * Two layouts supported, keyed separately:
 *   - "household" → /home (Home Edition front page)
 *   - "boat"      → /    (Båt-forsiden, WeeklyPlan intro widgets only)
 *
 * Storage key: `pb_layout_v2_{edition}`
 * Shape: {
 *   widgets: [{ id, hidden, size }],      // ordered list (drag-drop)
 *   density: "small" | "medium" | "large",
 *   columns: 1 | 2,
 *   customTiles: [{ id, label, icon, target }]
 * }
 */
import { useCallback, useEffect, useState } from "react";

const storageKey = (edition) => `pb_layout_v2_${edition}`;

export const WIDGET_META_HOUSEHOLD = {
  "week-dinners": { label: "Ukens middager", description: "Dagens + resten av uka" },
  "weather":      { label: "Værvarsel",     description: "MET.no basert på GPS" },
  "budget":       { label: "Matbudsjett",   description: "Månedlig forbruk + alarm" },
  "stats":        { label: "Statistikk",    description: "Oppskrifter / handleliste / AI / medlemmer" },
  "todo":         { label: "Oppgaver",      description: "Husstandens todo-liste" },
  "calendar":     { label: "Kalender",      description: "Kommende avtaler (7 dager)" },
  "actions":      { label: "Handlinger",    description: "Snarvei-kort (AI, matplan, oppskrifter…)" },
};

export const WIDGET_META_BOAT = {
  "weather":           { label: "Værvarsel",           description: "MET.no" },
  "expiry-warnings":   { label: "Utløpsvarsler",       description: "Varer i pantry som snart går ut" },
};

const DEFAULT_HOUSEHOLD = {
  widgets: [
    { id: "week-dinners", hidden: false, size: "large" },
    { id: "weather",      hidden: false, size: "medium" },
    { id: "calendar",     hidden: false, size: "medium" },
    { id: "budget",       hidden: false, size: "medium" },
    { id: "stats",        hidden: false, size: "medium" },
    { id: "todo",         hidden: false, size: "medium" },
    { id: "actions",      hidden: false, size: "medium" },
  ],
  density: "medium",
  columns: 1,
  customTiles: [],
};

const DEFAULT_BOAT = {
  widgets: [
    { id: "weather",         hidden: false, size: "medium" },
    { id: "expiry-warnings", hidden: false, size: "medium" },
  ],
  density: "medium",
  columns: 1,
  customTiles: [],
};

export const EDITION_CONFIG = {
  household: { meta: WIDGET_META_HOUSEHOLD, default: DEFAULT_HOUSEHOLD },
  boat:      { meta: WIDGET_META_BOAT,      default: DEFAULT_BOAT },
};

function validate(raw, edition) {
  const cfg = EDITION_CONFIG[edition] || EDITION_CONFIG.household;
  const validIds = Object.keys(cfg.meta);
  if (!raw || typeof raw !== "object") return cfg.default;
  const widgets = Array.isArray(raw.widgets) ? raw.widgets.filter((w) => validIds.includes(w.id)) : [];
  const present = new Set(widgets.map((w) => w.id));
  for (const id of validIds) {
    if (!present.has(id)) {
      const def = cfg.default.widgets.find((w) => w.id === id);
      widgets.push({ id, hidden: !!def?.hidden, size: def?.size || "medium" });
    }
  }
  return {
    widgets: widgets.map((w) => ({
      id: w.id,
      hidden: !!w.hidden,
      size: ["small", "medium", "large"].includes(w.size) ? w.size : "medium",
    })),
    density: ["small", "medium", "large"].includes(raw.density) ? raw.density : "medium",
    columns: raw.columns === 2 ? 2 : 1,
    customTiles: Array.isArray(raw.customTiles)
      ? raw.customTiles.filter((t) => t && t.id && t.label && t.target).slice(0, 12)
      : [],
  };
}

export function loadLayout(edition = "household") {
  try {
    // Legacy migration: v1 only existed for household
    if (edition === "household") {
      const legacy = localStorage.getItem("pb_home_layout_v1");
      const v2 = localStorage.getItem(storageKey("household"));
      if (legacy && !v2) {
        localStorage.setItem(storageKey("household"), legacy);
      }
    }
    const raw = localStorage.getItem(storageKey(edition));
    if (!raw) return (EDITION_CONFIG[edition] || EDITION_CONFIG.household).default;
    return validate(JSON.parse(raw), edition);
  } catch {
    return (EDITION_CONFIG[edition] || EDITION_CONFIG.household).default;
  }
}

export function saveLayout(layout, edition = "household") {
  try {
    localStorage.setItem(storageKey(edition), JSON.stringify(validate(layout, edition)));
  } catch { /* ignore */ }
}

export function useLayout(edition = "household") {
  const [layout, setLayout] = useState(() => loadLayout(edition));

  // Re-load when edition changes
  useEffect(() => { setLayout(loadLayout(edition)); }, [edition]);

  // Cross-tab sync
  useEffect(() => {
    const handler = (e) => {
      if (e.key === storageKey(edition) && e.newValue) {
        try { setLayout(validate(JSON.parse(e.newValue), edition)); } catch { /* ignore */ }
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [edition]);

  const update = useCallback((patch) => {
    setLayout((prev) => {
      const next = validate(typeof patch === "function" ? patch(prev) : { ...prev, ...patch }, edition);
      saveLayout(next, edition);
      return next;
    });
  }, [edition]);

  const reset = useCallback(() => {
    const def = (EDITION_CONFIG[edition] || EDITION_CONFIG.household).default;
    saveLayout(def, edition);
    setLayout(def);
  }, [edition]);

  return { layout, update, reset, meta: (EDITION_CONFIG[edition] || EDITION_CONFIG.household).meta };
}

// Backwards-compat re-exports for HomePage.jsx
export const useHomeLayout = () => useLayout("household");
export const WIDGET_META = WIDGET_META_HOUSEHOLD;
export const DEFAULT_LAYOUT = DEFAULT_HOUSEHOLD;

export function resolveSize(widget, density) {
  return widget?.size || density || "medium";
}
